import { useEffect, useState } from "react";
import { statusApi } from "../api/status";
import { toDatetimeLocal } from "../components/Status/statusFormat";
import { useAsync } from "./useAsync";
import type { IncidentDetail } from "../types/incident";

export interface IncidentOverrideForm {
  severity: string;
  status: string;
  resolvedAt: string;
}

export interface IncidentOverrideController {
  form: IncidentOverrideForm;
  reasoning: string;
  setField: (field: keyof IncidentOverrideForm, value: string) => void;
  setReasoning: (value: string) => void;
  save: () => Promise<void>;
  saving: boolean;
  saved: boolean;
}

function useIncidentOverride(
  incident: IncidentDetail | null,
  onSaved: () => void,
): IncidentOverrideController {
  const [form, setForm] = useState<IncidentOverrideForm>({ severity: "", status: "", resolvedAt: "" });
  const [reasoning, setReasoning] = useState("");
  const [saving, setSaving] = useState(false);
  const [saved, setSaved] = useState(false);

  useEffect(() => {
    if (!incident) return;
    setForm({
      severity: incident.severity,
      status: incident.status,
      resolvedAt: incident.resolved_at ? toDatetimeLocal(incident.resolved_at) : "",
    });
  }, [incident]);

  function setField(field: keyof IncidentOverrideForm, value: string) {
    setForm((current) => ({ ...current, [field]: value }));
  }

  async function save() {
    if (!incident || !reasoning.trim()) return;
    setSaving(true);
    try {
      await statusApi.overrideIncident(incident.id, {
        severity: form.severity,
        status: form.status,
        resolved_at: form.status === "resolved" && form.resolvedAt ? new Date(form.resolvedAt).toISOString() : null,
        reasoning: reasoning.trim(),
      });
      setSaved(true);
      setReasoning("");
      setTimeout(() => setSaved(false), 3000);
      onSaved();
    } catch {
      return;
    } finally {
      setSaving(false);
    }
  }

  return { form, reasoning, setField, setReasoning, save, saving, saved };
}

export function useIncidentDetail(id: string) {
  const { data, loading, error, reload } = useAsync(
    () => statusApi.getIncident(id).then((response) => response.data),
    [id],
    { errorMessage: "Could not load this incident" },
  );
  const override = useIncidentOverride(data ?? null, reload);
  return { data, loading, error, reload, override };
}
